import { useState } from "react";
import { CreditCard, ExternalLink } from "lucide-react";
import type { BillingClientSummary, BillingEventSummary } from "@school-sales-ai/shared";
import { useBillingOverview } from "@/hooks/useBilling";
import { formatMxn } from "@/lib/format-mxn";
import { StripeCustomerModal } from "@/components/StripeCustomerModal";
import { PaymentLinkModal } from "@/components/PaymentLinkModal";
import { SubscriptionModal } from "@/components/SubscriptionModal";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const TH = "text-[11px] font-semibold uppercase tracking-widest text-ink-500";

const DATE_FORMAT = new Intl.DateTimeFormat("es-MX", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return DATE_FORMAT.format(new Date(iso));
}

/**
 * Stripe's status vocabulary, translated for display.
 *
 * `past_due` is the only red row and the only reason this page exists — it is what
 * "a school's card bounced and nobody has noticed" looks like. It never means the
 * service was suspended: nothing here or in the webhook suspends anything.
 */
const STATUS_LABELS: Record<string, string> = {
  active: "Activa",
  trialing: "En prueba",
  past_due: "Vencida",
  unpaid: "Sin pagar",
  canceled: "Cancelada",
  incomplete: "Incompleta",
  incomplete_expired: "Expirada",
  paused: "Pausada",
};

const GOOD = "bg-[oklch(0.94_0.04_155)] text-[oklch(0.30_0.10_155)]";
const BAD = "bg-[oklch(0.95_0.03_16)] text-[oklch(0.42_0.16_16)]";
const NEUTRAL = "bg-ink-100 text-ink-500";

function statusTone(status: string | null): string {
  if (status === "active" || status === "trialing") return GOOD;
  if (status === "past_due" || status === "unpaid") return BAD;
  return NEUTRAL;
}

const PENDING = "bg-[oklch(0.95_0.05_75)] text-[oklch(0.42_0.11_75)]";

function StatusPill({
  status,
  cancelAt,
}: {
  status: string | null;
  cancelAt?: string | null;
}): React.JSX.Element {
  // A scheduled cancellation wins over the status. Stripe keeps reporting "active"
  // until the period actually ends, so "Activa" would be literally true and
  // practically misleading for a school leaving in two weeks.
  if (cancelAt) {
    return (
      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${PENDING}`}>
        Cancela el {formatDate(cancelAt)}
      </span>
    );
  }
  if (!status) {
    return (
      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${NEUTRAL}`}>
        Sin suscripción
      </span>
    );
  }
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${statusTone(status)}`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function EventRow({ event }: { event: BillingEventSummary }): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-xs">
      <span className="font-mono text-ink-500">{event.type}</span>
      <span className="text-ink-700">{formatDate(event.occurredAt)}</span>
      <span className="text-ink-900">{formatMxn(event.amountCents, event.currency)}</span>
      {event.hostedInvoiceUrl ? (
        <a
          href={event.hostedInvoiceUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-ink-700 underline"
        >
          Factura <ExternalLink size={11} />
        </a>
      ) : (
        <span className="text-ink-400">—</span>
      )}
    </div>
  );
}

export function Billing(): React.JSX.Element {
  const { data, isPending, isError } = useBillingOverview();
  const [linkTarget, setLinkTarget] = useState<BillingClientSummary | null>(null);
  const [payLinkTarget, setPayLinkTarget] = useState<BillingClientSummary | null>(null);
  const [subTarget, setSubTarget] = useState<BillingClientSummary | null>(null);

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-18 flex-shrink-0 items-center justify-between border-b border-ink-100 bg-surface px-6 py-4">
        <div>
          <h1 className="font-display text-[28px] font-extrabold leading-none text-ink-900">
            Facturación
          </h1>
          <p className="mt-0.5 text-xs text-ink-500">
            Estado de cobro de cada escuela. Los pagos se gestionan en Stripe.
          </p>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-6">
        {isError ? (
          <p className="text-sm text-[oklch(0.55_0.18_16)]">
            No se pudo cargar la facturación.
          </p>
        ) : isPending ? (
          <p className="text-sm text-ink-500">Cargando…</p>
        ) : data.clients.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-ink-100">
              <CreditCard size={22} className="text-ink-400" />
            </div>
            <p className="font-display text-lg font-bold text-ink-700">Aún no hay clientes</p>
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-ink-100 bg-surface shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="bg-ink-050 hover:bg-ink-050">
                    <TableHead className={TH}>Escuela</TableHead>
                    <TableHead className={TH}>Plan</TableHead>
                    <TableHead className={TH}>Estado</TableHead>
                    <TableHead className={TH}>Monto</TableHead>
                    <TableHead className={TH}>Próximo cobro</TableHead>
                    <TableHead className={TH}>Último pago</TableHead>
                    <TableHead className={TH}>Link de cobro</TableHead>
                    <TableHead className={`text-right ${TH}`}>Stripe</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.clients.map((client) => (
                    <TableRow key={client.clientId}>
                      <TableCell className="text-xs font-medium text-ink-900">
                        {client.clientName}
                      </TableCell>
                      <TableCell className="text-xs text-ink-700">
                        {client.planName ?? <span className="text-ink-400">—</span>}
                      </TableCell>
                      <TableCell>
                        <StatusPill status={client.subscriptionStatus} cancelAt={client.cancelAt} />
                      </TableCell>
                      <TableCell className="text-xs text-ink-900">
                        {formatMxn(client.currentPriceAmountCents, client.currentPriceCurrency)}
                        {/* The recurring amount is the Price's unit_amount, while
                            "Último pago" comes from the invoice and includes IVA.
                            Without a label the same row shows two numbers 16% apart
                            and nothing explains why — and which label is right
                            depends on how the client was quoted. Null predates the
                            per-client choice, and all of those were exclusive. */}
                        {client.currentPriceAmountCents !== null && (
                          <span className="ml-1 text-ink-400">
                            {client.taxBehavior === "inclusive" ? "IVA incluido" : "+ IVA"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-ink-700">
                        {formatDate(client.currentPeriodEnd)}
                      </TableCell>
                      <TableCell className="text-xs text-ink-700">
                        {client.lastPaidAt ? (
                          <>
                            {formatDate(client.lastPaidAt)}
                            <span className="ml-1 text-ink-400">
                              {formatMxn(client.lastPaidAmountCents, client.currentPriceCurrency)}
                            </span>
                          </>
                        ) : (
                          <span className="text-ink-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPayLinkTarget(client)}
                        >
                          {client.paymentLinkUrl ? "Ver link" : "Generar"}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          {client.stripeCustomerId ? (
                            <span className="font-mono text-[11px] text-ink-500">
                              {client.stripeCustomerId}
                            </span>
                          ) : (
                            <span className="text-[11px] text-ink-400">Sin vincular</span>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setLinkTarget(client)}
                          >
                            {client.stripeCustomerId ? "Cambiar" : "Vincular"}
                          </Button>
                          {/* Only offered where there is something to act on — a
                              client with no subscription has nothing to cancel. */}
                          {client.subscriptionStatus && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSubTarget(client)}
                            >
                              {client.cancelAt ? "Reactivar" : "Cancelar"}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {data.orphanEvents.length > 0 && (
              <div className="mt-6 rounded-lg border border-[oklch(0.88_0.06_75)] bg-[oklch(0.98_0.03_75)] p-4">
                <h2 className="font-display text-sm font-bold text-ink-900">
                  Eventos sin escuela ({data.orphanEvents.length})
                </h2>
                <p className="mt-0.5 text-xs text-ink-600">
                  Llegaron cobros de clientes de Stripe que no corresponden a ninguna escuela.
                  Al vincular ese Customer ID arriba, se adoptan automáticamente.
                </p>
                <div className="mt-3 divide-y divide-ink-100">
                  {data.orphanEvents.map((event) => (
                    <div key={event.id}>
                      <div className="pt-1.5 font-mono text-[11px] text-ink-500">
                        {event.stripeCustomerId ?? "(sin customer)"}
                      </div>
                      <EventRow event={event} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {linkTarget && (
        <StripeCustomerModal
          client={linkTarget}
          onClose={() => setLinkTarget(null)}
        />
      )}

      {payLinkTarget && (
        <PaymentLinkModal
          client={payLinkTarget}
          onClose={() => setPayLinkTarget(null)}
        />
      )}

      {subTarget && (
        <SubscriptionModal client={subTarget} onClose={() => setSubTarget(null)} />
      )}
    </div>
  );
}
